import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { API } from "../../lib/site";

const inputCls =
  "w-full rounded-xl border border-sand-border bg-sand-bg/50 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-reef/40 focus:border-reef transition";
const labelCls = "block font-mono text-[10px] tracking-[0.15em] uppercase text-slate-500 mb-1.5";

function Toggle({ checked, onChange, testid, label }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <input type="checkbox" data-testid={testid} checked={checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-[#865F34]" />
      <span className="text-sm font-medium text-slate-800">{label}</span>
    </label>
  );
}

export default function IntegrationsTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/settings`, { withCredentials: true }).then((res) => setS(res.data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, s, { withCredentials: true });
      toast.success("Integration settings saved.");
    } catch {
      toast.error("Could not save settings.");
    } finally {
      setSaving(false);
    }
  };

  if (!s) return <p className="text-sm text-slate-400 py-10 text-center font-mono animate-pulse">Loading…</p>;

  return (
    <div data-testid="admin-integrations-tab" className="space-y-6 max-w-3xl">
      <div className="bg-white border border-sand-border rounded-2xl p-6 sm:p-8">
        <h3 className="font-serif text-2xl text-slate-900">Live chat — Zoho SalesIQ</h3>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed">
          Adds the Zoho SalesIQ chat bubble to your website so visitors can chat with your team
          live — just like the WhatsApp button. You answer chats from the SalesIQ app.
        </p>
        <ol className="mt-3 text-sm text-slate-600 space-y-1.5 list-decimal list-inside">
          <li>In Zoho SalesIQ go to <strong>Settings → Brands → your brand → Installation → Website</strong>.</li>
          <li>Copy the <strong>widgetcode</strong> value from the generated snippet.</li>
          <li>Paste it below and switch the chat on — the bubble appears on the site instantly.</li>
        </ol>
        <div className="mt-5 space-y-4">
          <Toggle checked={s.salesiq_enabled} onChange={(v) => setS({ ...s, salesiq_enabled: v })} testid="admin-salesiq-enable-toggle" label="Show Zoho SalesIQ chat bubble on the website" />
          <div>
            <label className={labelCls}>SalesIQ widget code</label>
            <input
              data-testid="admin-salesiq-code-input"
              value={s.salesiq_widget_code}
              onChange={(e) => setS({ ...s, salesiq_widget_code: e.target.value })}
              placeholder="Paste the widgetcode from SalesIQ"
              className={inputCls}
            />
          </div>
        </div>
      </div>

      <div className="bg-white border border-sand-border rounded-2xl p-6 sm:p-8">
        <h3 className="font-serif text-2xl text-slate-900">WhatsApp auto-confirmation</h3>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed">
          The moment a customer books a free visit, they receive an automatic WhatsApp confirmation
          with their name, date and time slot. Choose your provider:
        </p>
        <div className="mt-4 flex gap-2">
          {[["doubletick", "DoubleTick (my current)"], ["twilio", "Twilio"]].map(([id, label]) => (
            <button
              key={id}
              type="button"
              data-testid={`admin-wa-provider-${id}`}
              onClick={() => setS({ ...s, wa_provider: id })}
              className={`px-5 py-2.5 rounded-full font-mono text-[11px] tracking-[0.12em] uppercase transition-colors ${
                s.wa_provider === id ? "bg-reef text-sand-bg" : "border border-sand-border text-slate-600 hover:border-reef/40"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {s.wa_provider === "doubletick" ? (
          <div className="mt-5 space-y-4">
            <ol className="text-sm text-slate-600 space-y-1.5 list-decimal list-inside">
              <li>In DoubleTick: <strong>Settings → Developer API → Create new key</strong> (copy it immediately).</li>
              <li>Create a template, e.g. <span className="font-mono text-xs">appointment_confirmation</span> (Utility, English) with body:
                <span className="font-mono text-xs block mt-1 bg-sand-soft rounded-lg px-3 py-2">Hi {"{{name}}"}, your Reef Deco free visit is confirmed for {"{{date}}"} ({"{{time}}"}). Reply here to reschedule.</span>
                Wait until Meta approves it (usually 24–48h).</li>
              <li>Enter the key, your DoubleTick WhatsApp number and the template name below.</li>
            </ol>
            <Toggle checked={s.wa_enabled} onChange={(v) => setS({ ...s, wa_enabled: v })} testid="admin-wa-enable-toggle" label="Send automatic WhatsApp confirmation after each booking" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>DoubleTick API key</label>
                <input data-testid="admin-dt-key-input" value={s.dt_api_key} onChange={(e) => setS({ ...s, dt_api_key: e.target.value })} placeholder={s.dt_api_key ? "" : "••••••••  (leave as-is to keep saved key)"} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>WhatsApp sender number</label>
                <input data-testid="admin-dt-from-input" value={s.dt_from_number} onChange={(e) => setS({ ...s, dt_from_number: e.target.value })} placeholder="+971XXXXXXXXX" className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Approved template name</label>
                <input data-testid="admin-dt-template-input" value={s.dt_template_name} onChange={(e) => setS({ ...s, dt_template_name: e.target.value })} placeholder="appointment_confirmation" className={inputCls} />
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-5 space-y-4">
            <ol className="text-sm text-slate-600 space-y-1.5 list-decimal list-inside">
              <li>Create a Twilio account and enable a WhatsApp sender.</li>
              <li>For business-initiated messages, create an approved Content Template with variables: <span className="font-mono text-xs">{"{{1}}"}</span> = name, <span className="font-mono text-xs">{"{{2}}"}</span> = date, <span className="font-mono text-xs">{"{{3}}"}</span> = time slot.</li>
            </ol>
            <Toggle checked={s.wa_enabled} onChange={(v) => setS({ ...s, wa_enabled: v })} testid="admin-wa-enable-toggle" label="Send automatic WhatsApp confirmation after each booking" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Twilio Account SID</label>
                <input data-testid="admin-wa-sid-input" value={s.twilio_sid} onChange={(e) => setS({ ...s, twilio_sid: e.target.value })} placeholder="ACxxxxxxxxxxxxxxxx" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Twilio Auth Token</label>
                <input data-testid="admin-wa-token-input" value={s.twilio_token} onChange={(e) => setS({ ...s, twilio_token: e.target.value })} placeholder={s.twilio_token ? "" : "••••••••  (leave as-is to keep saved token)"} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>WhatsApp sender number</label>
                <input data-testid="admin-wa-from-input" value={s.twilio_from} onChange={(e) => setS({ ...s, twilio_from: e.target.value })} placeholder="+14155238886" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Content Template SID (optional)</label>
                <input data-testid="admin-wa-template-input" value={s.twilio_template_sid} onChange={(e) => setS({ ...s, twilio_template_sid: e.target.value })} placeholder="HXxxxxxxxxxxxxxxxx" className={inputCls} />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white border border-sand-border rounded-2xl p-6 sm:p-8">
        <h3 className="font-serif text-2xl text-slate-900">Google Ads — booking conversions</h3>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed">
          Your Google tag (AW-18473512145) is already installed on every page. To count a Google Ads
          conversion each time a customer completes a booking:
        </p>
        <ol className="mt-3 text-sm text-slate-600 space-y-1.5 list-decimal list-inside">
          <li>In Google Ads: <strong>Goals → Conversions → New conversion action → Website</strong>.</li>
          <li>Category: <strong>Book appointment</strong>, name it e.g. "Free visit booking".</li>
          <li>Open the new action → <strong>Tag setup → Event snippet</strong> and copy the part after the slash in <span className="font-mono text-xs">send_to: 'AW-18473512145/XXXXXXXX'</span> — that's your conversion label.</li>
          <li>Paste the label below and switch it on.</li>
        </ol>
        <div className="mt-5 space-y-4">
          <Toggle checked={s.gads_enabled} onChange={(v) => setS({ ...s, gads_enabled: v })} testid="admin-gads-enable-toggle" label="Count a conversion when a booking is completed" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Google Ads ID</label>
              <input data-testid="admin-gads-id-input" value={s.gads_id} onChange={(e) => setS({ ...s, gads_id: e.target.value })} placeholder="AW-18473512145" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Booking conversion label</label>
              <input data-testid="admin-gads-label-input" value={s.gads_booking_label} onChange={(e) => setS({ ...s, gads_booking_label: e.target.value })} placeholder="e.g. abC12dEfGhI" className={inputCls} />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-sand-border rounded-2xl p-6 sm:p-8">
        <h3 className="font-serif text-2xl text-slate-900">CRM connection (Zoho CRM & others)</h3>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed">
          Every new booking and estimate request is automatically pushed to your CRM as a lead.
          Works with <strong>Zoho Flow</strong>, Zapier or Make:
        </p>
        <ol className="mt-3 text-sm text-slate-600 space-y-1.5 list-decimal list-inside">
          <li>In Zoho Flow, create a flow with a <strong>Webhook (JSON)</strong> trigger and copy its URL.</li>
          <li>Add a <strong>Zoho CRM → Create record (Leads)</strong> action and map the fields (name, phone, email, details).</li>
          <li>Paste the webhook URL below and switch the connection on.</li>
        </ol>
        <div className="mt-5 space-y-4">
          <Toggle checked={s.crm_enabled} onChange={(v) => setS({ ...s, crm_enabled: v })} testid="admin-crm-enable-toggle" label="Send new bookings & estimates to my CRM" />
          <div>
            <label className={labelCls}>CRM webhook URL (https)</label>
            <input
              data-testid="admin-crm-url-input"
              value={s.crm_webhook_url}
              onChange={(e) => setS({ ...s, crm_webhook_url: e.target.value })}
              placeholder="https://flow.zoho.com/…/webhook/…"
              className={inputCls}
            />
          </div>
        </div>
      </div>

      <button
        data-testid="admin-integrations-save-btn"
        onClick={save}
        disabled={saving}
        className="flex items-center gap-2 px-8 py-3 rounded-full bg-reef text-sand-bg font-mono text-[11px] tracking-[0.15em] uppercase hover:bg-reef-dark transition-colors disabled:opacity-60"
      >
        <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save integration settings"}
      </button>
    </div>
  );
}
